import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiError,
  type AuditDisclosurePreview,
  type AuditDisclosureRow,
} from '@chicare/shared';

import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { AdminError } from './AdminError';

/**
 * Handing the raw record to somebody with the right to ask for it.
 *
 * ── THE SHAPE OF THE PROCEDURE ──────────────────────────────────────────────
 *
 * Check the size, authorise it, then download. The authorisation is written
 * BEFORE the file exists, so an export somebody started and abandoned still
 * leaves a trace of having been asked for.
 *
 * ── WHY THE HASH IS ON THE SCREEN ───────────────────────────────────────────
 *
 * It is not security — anybody holding the file can recompute it. It is
 * provenance. Six months into a dispute, either the copy a regulator produces
 * hashes to the value shown here or it is not the file ChiCare gave them, and
 * that is a question somebody will eventually ask.
 *
 * ── AND WHY NAMES ARE OFF BY DEFAULT ────────────────────────────────────────
 *
 * An audit of HOW refunds were decided does not need to know which human being
 * filed each one. Handing over identifiable buyers "just in case" is how a data
 * protection breach gets committed with good intentions, so the switch is off
 * and turning it on is a deliberate act recorded with the reason.
 */
export function AuditDisclosureScreen() {
  const [creating, setCreating] = useState(false);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin', 'audit-disclosures'],
    queryFn: ({ signal }) => api.auditDisclosures(signal),
  });

  if (isError) {
    return (
      <AdminError title="We couldn't load the disclosures" error={error} onRetry={() => refetch()} />
    );
  }

  if (creating) {
    return <NewDisclosure domains={data?.domains ?? {}} onDone={() => setCreating(false)} />;
  }

  const rows = data?.disclosures ?? [];

  return (
    <div className="mx-auto w-full max-w-[1000px] px-4 py-6 xl:px-8">
      <header className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="m-0 font-display text-[26px] font-bold tracking-[-0.02em]">
            Audit disclosures
          </h1>
          <p className="m-0 mt-1 max-w-[64ch] text-[13px] leading-relaxed text-muted">
            The raw record, for a regulator, a court or an auditor — every action, its written
            reason, and what changed. Each disclosure is itself recorded, so the question
            &ldquo;who else has this&rdquo; always has an answer.
          </p>
        </div>

        <Button onClick={() => setCreating(true)}>Authorise a disclosure</Button>
      </header>

      {isLoading ? (
        <div className="space-y-1.5">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-[72px] w-full" radius="card" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title="Nothing has been disclosed"
          body="When a regulator or a court asks for the record, authorise it here. The file is built fresh, hashed, and never stored on a disk."
          action={<Button onClick={() => setCreating(true)}>Authorise a disclosure</Button>}
        />
      ) : (
        <div className="space-y-2">
          {rows.map((row) => (
            <DisclosureRow key={row.reference} row={row} />
          ))}
        </div>
      )}
    </div>
  );
}

function DisclosureRow({ row }: { row: AuditDisclosureRow }) {
  const [copied, setCopied] = useState(false);

  const download = useMutation({
    mutationFn: () => api.downloadAuditDisclosure(row.reference),
    onSuccess: ({ blob, filename }) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    },
  });

  const error = download.error instanceof ApiError ? download.error : null;

  return (
    <div
      data-testid={`disclosure-${row.reference}`}
      className="rounded-card border border-line bg-white px-4 py-3"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1 basis-[260px]">
          <p className="m-0 flex flex-wrap items-center gap-2">
            <span className="text-[14px] font-bold text-ink">{row.requested_by}</span>
            {row.includes_personal_data ? (
              <span className="rounded-pill bg-caution-tint px-2 py-[2px] text-[10.5px] font-bold text-caution-ink">
                Names included
              </span>
            ) : (
              <span className="rounded-pill bg-paper px-2 py-[2px] text-[10.5px] font-medium text-muted">
                Pseudonymised
              </span>
            )}
          </p>

          <p className="m-0 mt-0.5 font-mono text-[11px] text-faint">
            {row.reference}
            {row.authority_reference ? ` · ${row.authority_reference}` : ''} · {row.period_from} to{' '}
            {row.period_to}
          </p>

          <p className="m-0 mt-1.5 max-w-[70ch] text-[12.5px] leading-snug text-muted">
            {row.reason}
          </p>

          <p className="m-0 mt-1.5 flex flex-wrap gap-1">
            {row.scope.map((one) => (
              <span
                key={one}
                className="rounded-pill bg-paper px-1.5 py-[1px] font-mono text-[10px] text-muted"
              >
                {one}
              </span>
            ))}
          </p>
        </div>

        <div className="shrink-0 text-right">
          <p className="m-0 text-[11px] text-faint">
            authorised by {row.authorised_by ?? 'unknown'}
          </p>
          {row.delivered_at ? (
            <p className="m-0 mt-0.5 font-mono text-[12px] tabular-nums text-ink">
              {row.row_count} rows
            </p>
          ) : (
            <p className="m-0 mt-0.5 text-[11.5px] text-muted">Not downloaded yet</p>
          )}

          <Button
            size="sm"
            variant="grey"
            className="mt-2"
            disabled={download.isPending}
            onClick={() => download.mutate()}
          >
            {download.isPending ? 'Building…' : 'Download'}
          </Button>
        </div>
      </div>

      {/* Provenance, not security. Either their copy hashes to this or it is
          not the file we gave them. */}
      {row.sha256 ? (
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard?.writeText(row.sha256 ?? '');
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
          }}
          className="mt-2.5 flex w-full items-center gap-1.5 overflow-hidden rounded-btn border border-line bg-paper px-2.5 py-1.5 text-left"
        >
          <Icon name="shield" size={12} className="shrink-0 text-muted" />
          <span className="truncate font-mono text-[10.5px] text-muted">
            sha256 {row.sha256}
          </span>
          <span className="ml-auto shrink-0 text-[10.5px] font-semibold text-blue">
            {copied ? 'Copied' : 'Copy'}
          </span>
        </button>
      ) : null}

      {error ? (
        <p role="alert" className="m-0 mt-2 text-[12px] font-medium text-danger">
          {error.message}
        </p>
      ) : null}
    </div>
  );
}

// ── Authorising one ─────────────────────────────────────────────────────────

function NewDisclosure({
  domains,
  onDone,
}: {
  domains: Record<string, string>;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();

  const [requestedBy, setRequestedBy] = useState('');
  const [authorityReference, setAuthorityReference] = useState('');
  const [reason, setReason] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  /**
   * Null means "everything", not "nothing".
   *
   * This was initialised from `domains` — a prop that is EMPTY on first render
   * because the list query has not resolved yet. useState only reads an
   * initialiser once, so the scope stayed empty forever after the domains
   * arrived, and both buttons were disabled with no way to enable them.
   */
  const [scope, setScope] = useState<string[] | null>(null);
  const chosen = scope ?? Object.keys(domains);
  const [identify, setIdentify] = useState(false);
  const [preview, setPreview] = useState<AuditDisclosurePreview | null>(null);

  const look = useMutation({
    mutationFn: () =>
      api.previewAuditDisclosure({ period_from: from, period_to: to, scope: chosen }),
    onSuccess: setPreview,
  });

  const authorise = useMutation({
    mutationFn: () =>
      api.authoriseAuditDisclosure({
        requested_by: requestedBy.trim(),
        authority_reference: authorityReference.trim() || undefined,
        reason: reason.trim(),
        period_from: from,
        period_to: to,
        scope: chosen,
        includes_personal_data: identify,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'audit-disclosures'] });
      onDone();
    },
  });

  const error = [look.error, authorise.error].find(
    (one): one is ApiError => one instanceof ApiError,
  );

  const toggle = (key: string) => {
    setPreview(null);
    setScope((current) => {
      const now = current ?? Object.keys(domains);

      return now.includes(key) ? now.filter((one) => one !== key) : [...now, key];
    });
  };

  return (
    <div className="mx-auto w-full max-w-[620px] px-4 py-6 xl:px-8">
      <button
        type="button"
        onClick={onDone}
        className="mb-4 inline-flex min-h-11 items-center gap-1.5 bg-transparent p-0 text-[13px] font-semibold text-muted"
      >
        <Icon name="back" size={15} strokeWidth={2} />
        All disclosures
      </button>

      <h1 className="m-0 font-display text-[22px] font-bold tracking-[-0.02em]">
        Authorise a disclosure
      </h1>
      <p className="m-0 mt-1 text-[13px] leading-relaxed text-muted">
        This is written down before the file exists, so an export that is started and abandoned
        still leaves a record of having been asked for.
      </p>

      <div className="mt-4 space-y-3 rounded-card border border-line bg-white p-4 sm:p-5">
        <Input
          label="Who asked for it?"
          value={requestedBy}
          placeholder="NAFDAC · Federal High Court, Abuja · our own board"
          error={error?.fieldErrors.requested_by?.[0] ?? null}
          onChange={(event) => setRequestedBy(event.target.value)}
        />

        <Input
          label="Their reference (optional)"
          mono
          value={authorityReference}
          placeholder="FHC/ABJ/CS/2026/88"
          hint="A court order number, a regulator's query, a board minute."
          onChange={(event) => setAuthorityReference(event.target.value)}
        />

        <div className="grid gap-3 sm:grid-cols-2">
          <Input
            label="From"
            type="date"
            value={from}
            error={error?.fieldErrors.period_from?.[0] ?? null}
            onChange={(event) => {
              setFrom(event.target.value);
              setPreview(null);
            }}
          />
          <Input
            label="To"
            type="date"
            value={to}
            error={error?.fieldErrors.period_to?.[0] ?? null}
            onChange={(event) => {
              setTo(event.target.value);
              setPreview(null);
            }}
          />
        </div>

        <label className="block">
          <span className="mb-1.5 block text-[12.5px] font-semibold text-ink">
            The legal basis
          </span>
          <textarea
            value={reason}
            rows={3}
            data-testid="disclosure-reason"
            onChange={(event) => setReason(event.target.value)}
            placeholder="Disclosure ordered in the matter of a disputed refund decision."
            className="w-full rounded-inp border border-line bg-white px-3 py-2 text-[13.5px] leading-relaxed text-ink outline-none focus:border-blue"
          />
          <span className="mt-1 block text-[11.5px] leading-snug text-faint">
            Read by whoever reviews this disclosure later — including, possibly, a different
            regulator.
          </span>
        </label>
      </div>

      <p className="m-0 mt-5 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
        What to include
      </p>

      <div className="mt-2 overflow-hidden rounded-card border border-line bg-white">
        {Object.entries(domains).map(([key, label]) => (
          <label
            key={key}
            className={cn(
              'flex min-h-11 cursor-pointer items-center gap-2.5 border-b border-line-2 px-4 py-2.5 last:border-b-0',
              chosen.includes(key) ? 'bg-blue-tint/40' : 'bg-white',
            )}
          >
            <input
              type="checkbox"
              checked={chosen.includes(key)}
              data-testid={`scope-${key}`}
              onChange={() => toggle(key)}
              className="h-4 w-4 shrink-0"
            />
            <span className="text-[13px] text-ink">{label}</span>
          </label>
        ))}
      </div>

      <label className="mt-3 flex min-h-11 cursor-pointer items-start gap-2.5 rounded-card border border-caution bg-caution-tint px-4 py-3">
        <input
          type="checkbox"
          checked={identify}
          data-testid="include-personal"
          onChange={(event) => setIdentify(event.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0"
        />
        <span className="min-w-0 flex-1">
          <span className="block text-[13px] font-bold text-caution-ink">
            Include names, emails and IP addresses
          </span>
          <span className="mt-0.5 block text-[11.5px] leading-snug text-caution-ink">
            Off unless they have specifically asked. An audit of HOW decisions were made does not
            need to know which person filed each one — roles go either way, and &ldquo;a super
            admin approved this&rdquo; names nobody.
          </span>
        </span>
      </label>

      <Button
        variant="grey"
        className="mt-3"
        disabled={from === '' || to === '' || chosen.length === 0 || look.isPending}
        onClick={() => look.mutate()}
      >
        {look.isPending ? 'Counting…' : 'How big is it?'}
      </Button>

      {preview ? (
        <div
          data-testid="disclosure-preview"
          className="mt-3 rounded-card border border-blue bg-blue-tint/40 px-4 py-3.5"
        >
          <p className="m-0 text-[13px] font-bold text-blue">
            {preview.rows.toLocaleString('en-NG')} record
            {preview.rows === 1 ? '' : 's'} over that period.
          </p>
          {Object.keys(preview.actions ?? {}).length > 0 ? (
            <ul className="m-0 mt-2 list-none space-y-0.5 p-0">
              {Object.entries(preview.actions)
                .slice(0, 8)
                .map(([action, count]) => (
                  <li key={action} className="font-mono text-[11px] text-blue">
                    {action} · {count}
                  </li>
                ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      {error ? (
        <p
          role="alert"
          className="m-0 mt-3 rounded-card border border-danger bg-danger-tint px-3.5 py-2.5 text-[12.5px] font-medium text-danger"
        >
          {error.fieldErrors.reason?.[0] ?? error.message}
        </p>
      ) : null}

      <div className="mt-5 flex flex-wrap gap-2">
        <Button
          disabled={
            requestedBy.trim() === '' ||
            reason.trim().length < 20 ||
            from === '' ||
            to === '' ||
            authorise.isPending
          }
          onClick={() => authorise.mutate()}
        >
          {authorise.isPending ? 'Authorising…' : 'Authorise it'}
        </Button>

        <Button variant="grey" onClick={onDone}>
          Cancel
        </Button>
      </div>

      <p className="m-0 mt-3 text-[11.5px] leading-relaxed text-faint">
        Prescription images and refund evidence photographs never leave through this door (§9.15).
        What IS included is the record that somebody opened one, and who — which is the thing an
        audit is for. The file says so on its first line.
      </p>
    </div>
  );
}
